import { useState } from 'react';
import { useParams, Link } from 'wouter';
import { Show } from '@clerk/react';
import { useGetGame, useToggleGameLike, useToggleGameFavorite, useGetLeaderboard, getGetGameQueryKey, getGetLeaderboardQueryKey } from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, Star, Play, Calendar, Infinity, Trophy, ArrowLeft, CheckCircle2, Gamepad2, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { difficultyFor } from '@/lib/difficulty';

export default function GameDetail() {
  const params = useParams();
  const slug = params.slug!;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: detail, isLoading } = useGetGame(slug, { query: { enabled: !!slug, queryKey: getGetGameQueryKey(slug) } });
  // Hall of Fame toggle: one board at a time instead of stacked lists.
  const [boardMetric, setBoardMetric] = useState<'total' | 'best' | 'avg'>('total');
  const metricParam = boardMetric === 'total' ? undefined : boardMetric;
  const { data: leaderboardData } = useGetLeaderboard(
    { gameSlug: slug, metric: metricParam },
    { query: { enabled: !!slug, queryKey: getGetLeaderboardQueryKey({ gameSlug: slug, metric: metricParam }), staleTime: 0, refetchOnMount: 'always' } }
  );
  const leaderboard = leaderboardData?.entries;
  const myEntry = leaderboardData?.me;
  // The sidebar only shows the top 5, so "visible" means within that slice.
  const myEntryVisible = !!myEntry && !!leaderboard?.slice(0, 5).some((e) => e.username === myEntry.username);

  const MyRankRow = ({ entry, testId }: { entry: { rank: number; username: string; value: number }; testId: string }) => (
    <div className="mt-1 flex items-center justify-between gap-2 border border-secondary bg-secondary/10 p-3" data-testid={testId}>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 min-w-8 shrink-0 px-1 items-center justify-center border border-secondary bg-secondary text-secondary-foreground font-display text-sm font-bold">
          #{entry.rank}
        </div>
        <span className="truncate font-bold font-mono tracking-widest text-secondary">YOU</span>
      </div>
      <span className="shrink-0 font-bold font-mono text-secondary text-lg">{entry.value.toLocaleString()}</span>
    </div>
  );

  const toggleLike = useToggleGameLike({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetGameQueryKey(slug) });
      },
    },
  });

  const toggleFavorite = useToggleGameFavorite({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetGameQueryKey(slug) });
      },
    },
  });

  const handleLike = () => {
    toggleLike.mutate({ slug });
  };

  const handleFavorite = () => {
    toggleFavorite.mutate({ slug });
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <Skeleton className="mb-4 h-10 w-32 bg-primary/20" />
          <Skeleton className="mb-8 h-16 w-full max-w-2xl bg-muted/50 border border-primary/20 rounded-none" />
          <Skeleton className="mb-8 h-[400px] w-full rounded-none bg-muted/50 border border-primary/20" />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <Navbar />
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 flex flex-col items-center">
          <div className="rounded-none border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center animate-pulse">
            <p className="text-xl font-mono text-primary">GAME NOT FOUND</p>
          </div>
        </div>
      </div>
    );
  }

  const { game, todayPlayed, todayScore, archiveCount } = detail;

  return (
    <div className="min-h-[100dvh] bg-background">
      <Navbar />

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8 animate-slide-up">
        <Link href="/games">
          <Button variant="ghost" size="sm" className="mb-8 font-mono uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-none" data-testid="button-back">
            <ArrowLeft className="mr-2 h-4 w-4" />
            BACK TO LIBRARY
          </Button>
        </Link>

        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-8 border-b-2 border-primary/20 pb-8 relative">
              <div className="absolute bottom-[-2px] left-0 w-32 h-[2px] bg-primary glow-primary" />
              
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Badge variant="outline" className="bg-background text-primary border-primary/50 rounded-none uppercase tracking-widest text-[10px] font-bold px-3 py-1">
                  {game.sport}
                </Badge>
                <Badge variant="outline" className="bg-background text-secondary border-secondary/50 rounded-none uppercase tracking-widest text-[10px] font-bold px-3 py-1">
                  {game.gameType}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${difficultyFor(game.gameType).className} rounded-none uppercase tracking-widest text-[10px] font-bold px-3 py-1`}
                  data-testid="badge-difficulty"
                >
                  {difficultyFor(game.gameType).label}
                </Badge>
                {game.featured && <Badge className="bg-primary/20 text-primary border-none rounded-none uppercase tracking-widest text-[10px] font-bold px-3 py-1 glow-primary animate-pulse">HOT</Badge>}
              </div>
              <h1 className="mb-4 text-3xl font-black tracking-tighter sm:text-5xl font-display uppercase leading-[0.95] text-shadow-neon-cyan">
                {game.title}
              </h1>
              <p className="text-xl text-muted-foreground font-mono leading-relaxed">
                {game.description}
              </p>
            </div>

            {game.thumbnailUrl && (
              <div className="mb-10 overflow-hidden border-2 border-primary/30 relative group shadow-[0_0_30px_rgba(0,217,255,0.15)]">
                <div className="absolute inset-0 bg-primary/20 mix-blend-overlay pointer-events-none z-10" />
                {/* CRT Scanlines overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none z-20" />
                <img
                  src={game.thumbnailUrl}
                  alt={game.title}
                  className="w-full aspect-[21/9] object-cover filter contrast-125 saturate-150"
                />
              </div>
            )}

            <div className="mb-10 flex flex-wrap gap-4">
              {game.hasDaily && (
                <Link href={`/play/${slug}/daily`} className="flex-1 sm:flex-none">
                  <Button
                    size="lg"
                    className={`w-full h-16 px-8 text-lg font-display uppercase tracking-[0.2em] font-bold rounded-none border-2 transition-all ${todayPlayed ? 'bg-background border-primary text-primary glow-primary shadow-[inset_0_0_20px_rgba(0,217,255,0.2)]' : 'bg-primary text-primary-foreground border-primary glow-primary hover:-translate-y-1'}`}
                    disabled={todayPlayed}
                    data-testid="button-play-daily"
                  >
                    {todayPlayed ? (
                      <>
                        <CheckCircle2 className="mr-3 h-6 w-6 text-primary" />
                        COMPLETED ({todayScore} PTS)
                      </>
                    ) : (
                      <>
                        <Calendar className="mr-3 h-6 w-6" />
                        PLAY DAILY
                      </>
                    )}
                  </Button>
                </Link>
              )}
              {archiveCount > 0 && (
                <Link href={`/play/${slug}/unlimited`} className="flex-1 sm:flex-none">
                  <Button size="lg" variant="outline" className="w-full h-16 px-8 text-lg font-display uppercase tracking-[0.2em] font-bold border-2 border-secondary/50 text-secondary hover:bg-secondary/10 hover:border-secondary rounded-none transition-all" data-testid="button-play-unlimited">
                    <Infinity className="mr-3 h-6 w-6" />
                    PLAY
                  </Button>
                </Link>
              )}
            </div>

            <Show when="signed-in">
              <div className="mb-10 flex gap-4">
                <Button
                  variant={game.liked ? 'default' : 'outline'}
                  size="sm"
                  onClick={handleLike}
                  disabled={toggleLike.isPending}
                  className={`rounded-none h-12 px-6 font-bold uppercase tracking-widest transition-all ${game.liked ? 'bg-secondary text-secondary-foreground glow-secondary border-secondary' : 'border-secondary/30 text-secondary hover:bg-secondary/10 hover:border-secondary'}`}
                  data-testid="button-like"
                >
                  <Heart className={`mr-2 h-5 w-5 ${game.liked ? 'fill-current' : ''}`} />
                  {game.likeCount.toLocaleString()} LIKES
                </Button>
                <Button
                  variant={game.favorited ? 'default' : 'outline'}
                  size="sm"
                  onClick={handleFavorite}
                  disabled={toggleFavorite.isPending}
                  className={`rounded-none h-12 px-6 font-bold uppercase tracking-widest transition-all ${game.favorited ? 'bg-primary text-primary-foreground glow-primary border-primary' : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary'}`}
                  data-testid="button-favorite"
                >
                  <Star className={`mr-2 h-5 w-5 ${game.favorited ? 'fill-current' : ''}`} />
                  {game.favorited ? 'SAVED' : 'Game saved'}
                </Button>
              </div>
            </Show>

            <div className="grid grid-cols-3 gap-4 border border-primary/30 bg-primary/5 p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
              <div className="text-center border-r border-primary/20">
                <div className="mb-2 text-3xl font-black text-primary font-display tracking-tighter">
                  {game.playCount.toLocaleString()}
                </div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">PLAYS</div>
              </div>
              <div className="text-center border-r border-primary/20">
                <div className="mb-2 text-3xl font-black text-secondary font-display tracking-tighter">
                  {game.likeCount.toLocaleString()}
                </div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">UPVOTES</div>
              </div>
              <div className="text-center">
                <div className="mb-2 text-3xl font-black text-primary font-display tracking-tighter">
                  {archiveCount}
                </div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">PUZZLES</div>
              </div>
            </div>
          </div>

          <div>
            <div className="border border-secondary/30 bg-background relative sticky top-28">
              <div className="absolute inset-0 bg-[linear-gradient(hsl(280_100%_60%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(280_100%_60%_/_0.1)_1px,_transparent_1px)] bg-[size:10px_10px] opacity-30 pointer-events-none" />
              <div className="relative z-10">
                <div className="p-6 border-b border-secondary/30 bg-secondary/5 flex items-center gap-3">
                  <Trophy className="h-6 w-6 text-secondary" />
                  <h2 className="text-xl font-bold font-display uppercase tracking-widest text-secondary">
                    HALL OF FAME
                  </h2>
                </div>
                <div className="p-2">
                  <div className="mb-1 grid grid-cols-3 gap-1 p-1" data-testid="toggle-board-metric">
                    {([
                      { key: 'total', label: 'All-Time' },
                      { key: 'best', label: 'Best Game' },
                      { key: 'avg', label: 'Avg/Game' },
                    ] as const).map((m) => (
                      <button
                        key={m.key}
                        onClick={() => setBoardMetric(m.key)}
                        className={`border px-2 py-2 text-[10px] font-mono font-bold uppercase tracking-widest transition-colors ${
                          boardMetric === m.key
                            ? 'border-secondary bg-secondary text-secondary-foreground glow-secondary'
                            : 'border-secondary/30 text-secondary hover:bg-secondary/10'
                        }`}
                        data-testid={`toggle-metric-${m.key}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  {leaderboard && leaderboard.length > 0 ? (
                    <div className="space-y-1">
                      {leaderboard.slice(0, 5).map((entry) => (
                        <Link key={entry.username} href={`/u/${entry.username}`}>
                          <div className="flex items-center justify-between gap-2 p-3 hover:bg-secondary/10 transition-colors cursor-pointer group">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center border font-display text-lg font-bold ${entry.rank === 1 ? 'bg-secondary border-secondary text-secondary-foreground glow-secondary' : 'bg-background border-secondary/50 text-secondary group-hover:bg-secondary/20'}`}>
                                {entry.rank}
                              </div>
                              <div className="flex min-w-0 items-center gap-3">
                                <Avatar className="h-8 w-8 shrink-0 border border-secondary/30 rounded-none">
                                  <AvatarImage src={entry.avatarUrl || undefined} />
                                  <AvatarFallback className="bg-background text-secondary font-mono text-xs rounded-none">
                                    {entry.username[0].toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate font-bold font-mono tracking-widest group-hover:text-secondary transition-colors">{entry.username}</span>
                              </div>
                            </div>
                            <span className="shrink-0 font-bold font-mono text-secondary text-lg">
                              {entry.value.toLocaleString()}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-sm font-mono uppercase tracking-widest text-muted-foreground animate-pulse">NO RECORDS FOUND</p>
                    </div>
                  )}
                  {myEntry && !myEntryVisible && <MyRankRow entry={myEntry} testId="row-my-rank" />}
                  <Show when="signed-out">
                    <div className="mt-2 border-t border-secondary/20 p-3 text-center" data-testid="nudge-sign-in-rank">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        <span className="text-secondary">SIGN IN</span> TO SEE YOUR RANK
                      </p>
                    </div>
                  </Show>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
